import { useCallback, useSyncExternalStore } from 'react';

// Small pieces of view state that live as long as the page is open and are
// shared between views (which games are collapsed, which hidden games have been
// shown), but are not saved. Each named store is a set of ids.

const sets = new Map<string, ReadonlySet<string>>();
const listeners = new Map<string, Set<() => void>>();
const EMPTY: ReadonlySet<string> = new Set();

const listenersOf = (name: string) => {
    let found = listeners.get(name);
    if (found === undefined) {
        found = new Set();
        listeners.set(name, found);
    }
    return found;
};

export function useEphemeralSet(name: string): [ReadonlySet<string>, (id: string, member: boolean) => void] {
    const subscribe = useCallback((listener: () => void) => {
        const group = listenersOf(name);
        group.add(listener);
        return () => { group.delete(listener); };
    }, [name]);
    const current = useSyncExternalStore(subscribe, () => sets.get(name) ?? EMPTY);

    const set = useCallback((id: string, member: boolean) => {
        const before = sets.get(name) ?? EMPTY;
        if (before.has(id) === member) {
            return;
        }
        const next = new Set(before);
        if (member) {
            next.add(id);
        } else {
            next.delete(id);
        }
        sets.set(name, next);
        listenersOf(name).forEach(listener => listener());
    }, [name]);

    return [current, set];
}

// For tests: forget everything.
export function resetEphemeralState(): void {
    sets.clear();
}
